/**
 * Derived context for the visual menu editors.
 *
 * Centralises the four Version lookups (layout, styles, resources, templates)
 * plus the UUID maps that every editor builds. Pure functions, no Vue, no state.
 *
 * Components call these from `computed` properties so Vue can still react to
 * changes in `menu` or `version`.
 */

/**
 * Layout values for the active menu (columns_number, padding, spacing, height, ...).
 * Returns an empty object if no layout matches the menu's type.
 *
 * @param {object} menu
 * @param {object} version
 * @returns {object}
 */
export function getMenuLayout(menu, version) {
    const found = version.layoutByType(menu.type)
    return found ? found.values : {}
}

/**
 * Style values for the active menu's time-of-year (primary_color, home_font, ...).
 * Returns an empty object if no styles match.
 *
 * @param {object} menu
 * @param {object} version
 * @returns {object}
 */
export function getMenuStyles(menu, version) {
    const found = version.styles.find(s => s.timeOfYear === menu.timeofyear)
    return found ? found.values : {}
}

/**
 * CDN resource URLs available for the menu's time-of-year. Android only today.
 *
 * @param {object} menu
 * @param {object} version
 * @returns {string[]}
 */
export function getMenuResources(menu, version) {
    return version.resourcesByPlatformAndTimeofyear('android', menu.timeofyear)
}

/**
 * Templates that pass the per-menu-type + time-of-year filter (i.e. the
 * templates valid for *new* items in this menu).
 *
 * @param {object} menu
 * @param {object} version
 * @returns {object[]}
 */
export function getMenuTemplates(menu, version) {
    return version.templatesByTimeofyearAndType(menu.timeofyear, menu.type)
}

/**
 * UUID → template map over ALL version.templates (not just the menu-filtered
 * ones). Needed because items may reference templates outside the current
 * menu's filter — e.g. legacy items in a submenu.
 *
 * @param {object} version
 * @returns {Object<string, object>}
 */
export function buildTemplateByUuid(version) {
    const map = {}
    version.templates.forEach(t => { map[t.uuid] = t })
    return map
}

/**
 * UUID → item map walking the menu's items + submenu_items recursively.
 * Used for O(1) lookups during navigation, drag-and-drop, and breadcrumb
 * resolution.
 *
 * @param {object} menu
 * @returns {Object<string, object>}
 */
export function buildItemsByUuid(menu) {
    const map = {}
    const walk = (list) => list.forEach(item => {
        map[item.uuid] = item
        if (item.submenu_items && item.submenu_items.length) walk(item.submenu_items)
    })
    walk(menu.items || [])
    return map
}

/**
 * Returns the parent UUID for an item, or `null` if the item is at the root,
 * or `undefined` if the item isn't in the tree.
 *
 * @param {string} uuid
 * @param {object} menu
 * @returns {string|null|undefined}
 */
export function findItemParentUuid(uuid, menu) {
    const walk = (items, parentUuid) => {
        for (const item of items) {
            if (item.uuid === uuid) return parentUuid
            const children = item.submenu_items
            if (children && children.length) {
                const result = walk(children, item.uuid)
                if (result !== undefined) return result
            }
        }
        return undefined
    }
    return walk(menu.items || [], null)
}

/**
 * Walks the entire item tree and assigns a sequential `ordering` field.
 * Mutates the items in place (matches the legacy contract used at save time).
 *
 * @param {object[]} items
 */
export function recomputeOrdering(items) {
    let ordering = 0
    const walk = (list) => {
        for (const item of list) {
            item.ordering = ++ordering
            if (item.submenu_items && item.submenu_items.length) walk(item.submenu_items)
        }
    }
    walk(items)
}

/**
 * Bundles every derived value into a single object. Useful when a consumer
 * wants the whole context in one go (avoids defining 7+ computed properties).
 *
 * @param {object} menu
 * @param {object} version
 */
export function getMenuContext(menu, version) {
    return {
        layout:         getMenuLayout(menu, version),
        styles:         getMenuStyles(menu, version),
        resources:      getMenuResources(menu, version),
        templates:      getMenuTemplates(menu, version),
        templateByUuid: buildTemplateByUuid(version),
        itemsByUuid:    buildItemsByUuid(menu),
        languages:      version.languages,
        mainLanguage:   version.mainLanguage(),
    }
}
